import { Component, EventEmitter, OnDestroy, OnInit, Output } from "@angular/core";
import { Organization } from "../../../../../organization/dto/Organization";
import { MetricMiniWithCompanies } from "../../../../dto/Metric";
import { MetricDataRole, MetricDataRoleMap } from "../../../../dto/MetricData";
import { SpinnerProps } from "../../../../../shared/component/modal-container/modal-container.component";
import { OrganizationsService } from "../../../../../organization/components/service/organizations.service";
import { KpiBusinessService } from "../../../../service/backend/kpi-business.service";
import { firstValueFrom, forkJoin, Subject } from "rxjs";
import { filter, map, switchMap, takeUntil, tap } from "rxjs/operators";
import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import { FormArray, FormControl, FormGroup } from "@angular/forms";
import { UUID } from "../../../../../../core/pojo/id-variable";
import { MetricGroup } from "../../../../dto/MetricGroup";
import {
  SelectorAutocompletePickerWrapper,
} from "../../../../../shared/component/input/selector-autocomplete/selector-autocomplete.component";
import { fadeInOutAnimation } from "../../../../../../util/animations/fade-in-out.animation";
import {
  CrossCompanyAnalysisCompaniesDTO,
  CrossCompanyAnalysisDto,
  CrossCompanyAnalysisMetricsDTO,
  KpiCrossCompanyService,
} from "../service/kpi-cross-company.service";
import { SharedComponentsModule } from "../../../../../shared/module/shared-components.module";
import { CompanyBackendService } from "../../../../../pp/service/company-backend.service";
import { CheckBoxSelector } from "../../../../../shared/component/checkbox-selector/checkbox-selector.component";
import {
  SelectorAutocompleteDeferredComponent,
} from "../../../../../shared/component/input/selector-autocomplete-deffered/selector-autocomplete-deferred.component";
import { DeferredCompanyAmountHelper } from "./deferred-company-amount.helper";

@Component({
  selector: 'app-kpi-cross-company-crud',
  templateUrl: './kpi-cross-company-crud.component.html',
  styleUrls: ['./kpi-cross-company-crud.component.scss'],
  animations: [fadeInOutAnimation(200, 0)],
  standalone: true,
  imports: [
    SharedComponentsModule,
    SelectorAutocompleteDeferredComponent,
  ],
})
export class KpiCrossCompanyCrudComponent implements OnInit, OnDestroy {
  private _destroyStream = new Subject<void>();

  protected readonly MetricDataRole = MetricDataRole;

  @Output() closeModal = new EventEmitter<boolean>();

  selectedOrg: Organization;
  crossCompanyAnalysis: CrossCompanyAnalysisDto;
  metricGroups: MetricGroup[];
  formGroup: FormGroupWrapper;

  metricsForSelector: Map<SelectorAutocompletePickerWrapper, MetricMiniWithCompanies>;
  metricDataRolesForSelector = MetricDataRoleMap;

  companiesForSelector: CheckBoxSelector[];
  selectedCompanies: CheckBoxSelector[] = [];

  spinnerProps: SpinnerProps = {
    isSpinnerActive: false,
    notActiveOnApply: true,
  };

  deferredCompanyAmountHelper: DeferredCompanyAmountHelper;
  getAmountOfCompaniesWithData: (item: SelectorAutocompletePickerWrapper, metric:MetricMiniWithCompanies) => Promise<void>;

  constructor(
    private orgService: OrganizationsService,
    private kpiService: KpiBusinessService,
    private crossCompanyService: KpiCrossCompanyService,
    private companyBackendService: CompanyBackendService) {
    this.deferredCompanyAmountHelper = new DeferredCompanyAmountHelper(kpiService);

    // this is a trick to make ngOnChanges not trigger all the time
    this.getAmountOfCompaniesWithData = (item: SelectorAutocompletePickerWrapper, metric: MetricMiniWithCompanies): Promise<void> => {
      return this.deferredCompanyAmountHelper.getAmountOfCompaniesWithData(item, metric);
    };
  }

  ngOnInit(): void {
    this.initSubscriptions();
    this.deferredCompanyAmountHelper.setupMetricBatching();
  }

  ngOnDestroy(): void {
    this.deferredCompanyAmountHelper.cancel();
    this._destroyStream.next();
    this._destroyStream.complete();
  }

  close(isSuccessful: boolean): void {
    this.closeModal.emit(isSuccessful);
  }

  async update(): Promise<void> {
    const analysis = this.formGroup.crossCompanyAnalysisForm.getRawValue();
    // metrics must be unique as they're primary keys for cross company analysis.
    // not using set as we have to compare the metricUUIDs.
    analysis.metrics = analysis.metrics
      .filter((metric) => metric.metricUUID)
      .reduce((acc, metric) => {
        if (!acc.some((m) => m.metricUUID === metric.metricUUID)) {
          acc.push(metric);
        }
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return acc;
      }, []);
    analysis.companies = this.selectedCompanies.map((c) => {
      return { name: c.item, companyUUID: c.UUID };
    });
    this.deferredCompanyAmountHelper.cancel();
    await firstValueFrom(this.crossCompanyService.saveAnalysis(analysis));
    this.close(true);
  }

  updateSelectedCompanies(companies: CheckBoxSelector[]): void {
    this.selectedCompanies = companies.filter((company) => company.chosen);
  }

  private initSubscriptions(): void {
    this.orgService.selected$
      .pipe(
        filter((org) => Boolean(org)),
        tap((org) => {
          this.selectedOrg = org;
          this.deferredCompanyAmountHelper.selectedOrg = org;
        }),
        switchMap(() => forkJoin([
          this.getMetricGroups(),
          this.getCrossCompanyAnalysis(),
          this.getCompanies(),
        ])),
        tap(() => this.setCrossCompanyAnalysisFormGroup()),
        tap(() => this.setChosenCompanies()),
        takeUntil(this._destroyStream))
      .subscribe();
  }

  private async getCrossCompanyAnalysis(): Promise<void> {
    try {
      this.crossCompanyAnalysis = await firstValueFrom(
        this.crossCompanyService
          .getAnalysis(this.selectedOrg.uuid)
          .pipe(takeUntil(this._destroyStream),));
    } catch (error) {
      if (error.includes('404')) {
        this.crossCompanyAnalysis = { companies: [], metrics: [], orgUUID: this.selectedOrg.uuid };
        return;
      }
      console.error(error);
      throw new Error('Error while getting cross company analysis');
    }
  }

  private setChosenCompanies(): void {
    this.companiesForSelector.forEach((c) => {
      c.chosen = this.crossCompanyAnalysis.companies
        .some((other) => other.companyUUID === c.UUID);
    });
    this.updateSelectedCompanies(this.companiesForSelector);
  }

  private setCrossCompanyAnalysisFormGroup(): void {
    this.formGroup = new FormGroupWrapper(this.crossCompanyAnalysis, this.selectedOrg, this.metricsForSelector);
  }

  private async getMetricGroups(): Promise<void> {
    this.metricGroups = await firstValueFrom(this.kpiService.getMetricGroups(this.selectedOrg.uuid)
      .pipe(takeUntil(this._destroyStream)));
    this.prepareMetricSelector();
  }

  private async getCompanies(): Promise<void> {
    this.companiesForSelector = await firstValueFrom(
      this.companyBackendService.getWithMetricsByOrg(this.selectedOrg.uuid)
        .pipe(
          map((dto) =>
            dto.map((c) => {
              return {
                UUID: c.uuid,
                item: c.name,
                chosen: false,
              };
            })),
          takeUntil(this._destroyStream),
        )
    );
  }

  /**
   * Creates a metric map for the selector.
   * Example:
   * 'ARR • Starting'
   * 'ARR • Ending'
   */
  private prepareMetricSelector(): void {
    this.metricsForSelector = new Map();
    this.metricGroups.forEach((metricGroup) => {
      const allMetrics = metricGroup.metrics.reduce((name, m2) => name + ', ' + m2.name, '');
      this.metricsForSelector.set(new SelectorAutocompletePickerWrapper(null, metricGroup.name, allMetrics, true), null);
      metricGroup.metrics.forEach((metric) => {
        const metricMini = MetricMiniWithCompanies.fromMetric(metric, metricGroup);
        this.metricsForSelector.set(
          new SelectorAutocompletePickerWrapper(metricGroup.name + ' • ' + metric.name, metricGroup.name, metric.name, false, DeferredCompanyAmountHelper.companiesToComment(metricMini)), metricMini);
      });
    });
  }
}

class FormGroupWrapper {
  crossCompanyAnalysisForm: CrossCompanyAnalysisFormGroup;
  metrics: MetricsFormArray;
  companies: CompaniesFormArray;

  constructor(
    private crossCompanyAnalysis: CrossCompanyAnalysisDto,
    private selectedOrg: Organization,
    private metricsForSelector: Map<SelectorAutocompletePickerWrapper, MetricMiniWithCompanies>,) {
    this.setCrossCompanyAnalysis();
  }

  drop(event: CdkDragDrop<FormGroup[]>): void {
    moveItemInArray(this.metrics.controls, event.previousIndex, event.currentIndex);
  }

  deleteMetric(index: number): void {
    this.metrics.removeAt(index);
  }

  duplicateMetric(index: number): void {
    const controlToCopy = this.metrics.at(index);
    const newControl = this.createEmptyMetricFormGroup();
    newControl.patchValue(controlToCopy.value);
    newControl.controls.metricUUID.patchValue(null);
    this.metrics.controls.splice(index, 0, newControl);
  }

  addMetric(): void {
    this.metrics.controls.push(this.createEmptyMetricFormGroup());
  }

  setMetric(indexToUpdate: number, newMetricWrapper: MetricMiniWithCompanies): void {
    const metricFormGroup = this.metrics.at(indexToUpdate).controls;
    metricFormGroup.metricUUID.setValue(newMetricWrapper.metric.uuid);
    metricFormGroup.name.setValue(newMetricWrapper.metric.name);
    metricFormGroup.groupName.setValue(newMetricWrapper.metric.groupName);
  }

  private createEmptyMetricFormGroup(): MetricFormGroup {
    return new FormGroup({
      metricUUID: new FormControl<UUID>(null),
      role: new FormControl<MetricDataRole>(MetricDataRole.ACTUAL),
      name: new FormControl<string>(null),
      groupName: new FormControl<string>(null),
    });
  }

  private setCrossCompanyAnalysis(): void {
    const isProvided = Boolean(this.crossCompanyAnalysis && this.crossCompanyAnalysis.metrics.length);
    this.metrics = new FormArray(
      isProvided ?
        this.crossCompanyAnalysis.metrics.map((metric) => this.getMetricFormGroup(metric))
        : []);

    this.companies = new FormArray(
      isProvided
        ? this.crossCompanyAnalysis.companies.map((company) => this.getCompanyFormGroup(company))
        : []);
    this.crossCompanyAnalysisForm = new FormGroup({
      metrics: this.metrics,
      orgUUID: new FormControl(this.selectedOrg.uuid),
      companies: this.companies,
    });
  }

  private getMetricFormGroup(metricsDTO: CrossCompanyAnalysisMetricsDTO): MetricFormGroup {
    const correspondingMetric = Array.from(this.metricsForSelector.values()).find((v) =>
      v && v.metric.uuid === metricsDTO.metricUUID
    );
    if (!correspondingMetric) {
      return null;
    }
    return new FormGroup({
      metricUUID: new FormControl<UUID>(metricsDTO.metricUUID),
      role: new FormControl<MetricDataRole>(metricsDTO.role),
      name: new FormControl<string>(correspondingMetric.metric.name),
      groupName: new FormControl<string>(correspondingMetric.metric.groupName),
    });
  }

  private getCompanyFormGroup(companiesDTO: CrossCompanyAnalysisCompaniesDTO): CompaniesFormGroup {
    return new FormGroup({
      companyUUID: new FormControl(companiesDTO.companyUUID),
      name: new FormControl(),
    });
  }
}

type CrossCompanyAnalysisFormGroup = FormGroup<{
  orgUUID: FormControl<UUID>;
  metrics: MetricsFormArray;
  companies: CompaniesFormArray;
}>;

type MetricsFormArray = FormArray<MetricFormGroup>;

type MetricFormGroup = FormGroup<{
  metricUUID: FormControl<UUID>;
  name: FormControl<string>;
  role: FormControl<MetricDataRole>;
  groupName: FormControl<string>;
}>;

type CompaniesFormArray = FormArray<CompaniesFormGroup>;

type CompaniesFormGroup = FormGroup<{
  companyUUID: FormControl<UUID>;
  name: FormControl<string>;
}>;
